import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Grid, IconButton, InputAdornment, Typography } from '@mui/material';
import {
  CheckboxInputField,
  ControlledField,
  FormControlGroup,
  PhoneInputField,
  colors,
} from 'remote/global';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Stack } from '@mui/system';
import { RemoveRedEyeOutlined } from '@mui/icons-material';
import { usePostRegistrationOperatorUserMutation } from 'api/queries/registration';
import { ROUTES } from '@/utils/data/routes';
import {
  AccountInvitationRegistrationSchemaType,
  accountInvitationRegistrationSchema,
} from './types';

// We technically can create reusable components from AccountRegistrationForm but I'd rather keep these separate
function AccountInvitationRegistrationForm({ defaultEmail }: { defaultEmail: string }) {
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const { id } = useParams();
  const tInstance = useTranslation();
  const { t, i18n } = tInstance;
  const navigate = useNavigate();

  const {
    handleSubmit,
    control,
    formState: { isSubmitting, isValidating, isLoading, errors },
  } = useForm<AccountInvitationRegistrationSchemaType>({
    defaultValues: {
      contactInfo: {
        firstName: '',
        lastName: '',
        email: defaultEmail,
        phone: '',
      },
      password: '',
      confirmPassword: '',
      termsAccepted: undefined,
      lang: i18n.language,
      uuid: id!,
    },
    resolver: zodResolver(accountInvitationRegistrationSchema),
  });
  const postRegistrationOperatorUserMutation = usePostRegistrationOperatorUserMutation();

  const onValid = async (data: AccountInvitationRegistrationSchemaType) => {
    await postRegistrationOperatorUserMutation.mutateAsync({ ...data, lang: i18n.language }); // We readd the language because it can change during the lifecycle of this component
    navigate(ROUTES.BaseRoutes.ROOT, { replace: true });
  };

  return (
    <form onSubmit={handleSubmit(onValid)}>
      <Grid container spacing={6}>
        <Grid container item xs={12} md={6} gap={2}>
          <Grid item xs={12}>
            <Typography
              sx={{
                color: colors.uiBlue600,
                fontWeight: 600,
                fontSize: '18px',
              }}
            >
              {t('SignUpView.steps.account.title')}
            </Typography>
            <Typography
              sx={{
                color: colors.gray1,
              }}
              variant='body2'
            >
              {t('SignUpView.steps.account.subtitle')}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Stack direction='column' spacing={1}>
              <ControlledField
                control={control}
                name='contactInfo.email'
                translationKey='personalEmail'
                fieldProps={{
                  disabled: defaultEmail != null,
                  InputProps: {
                    type: 'email',
                    autoComplete: 'email',
                    readOnly: defaultEmail != null,
                  },
                }}
              />
              <ControlledField
                control={control}
                name='contactInfo.firstName'
                translationKey='firstName'
                fieldProps={{ InputProps: { autoComplete: 'given-name' } }}
                focusInitial
              />
              <ControlledField
                control={control}
                name='contactInfo.lastName'
                translationKey='lastName'
                fieldProps={{ InputProps: { autoComplete: 'family-name' } }}
              />
              <ControlledField
                control={control}
                name='contactInfo.phone'
                render={(renderProps) => (
                  <PhoneInputField {...renderProps} translationKey='phone' />
                )}
              />
            </Stack>
          </Grid>
        </Grid>
        <Grid container item xs={12} md={6} gap={2}>
          <Grid item xs={12}>
            <Typography
              sx={{
                color: colors.uiBlue600,
                fontWeight: 600,
                fontSize: '18px',
              }}
            >
              {t('SignUpView.steps.password.title')}
            </Typography>
            <Typography
              sx={{
                color: colors.gray1,
              }}
              variant='body2'
            >
              {t('SignUpView.steps.account.subtitle')}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Stack direction='column' spacing={1}>
              <ControlledField
                control={control}
                name='password'
                fieldProps={{
                  InputProps: {
                    type: showPasswordFields ? 'text' : 'password',
                    autoComplete: 'password',
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton
                          tabIndex={-1}
                          onMouseDown={() => setShowPasswordFields(true)}
                          onMouseUp={() => setShowPasswordFields(false)}
                          onMouseLeave={() => setShowPasswordFields(false)}
                          onTouchStart={() => setShowPasswordFields(true)}
                          onTouchEnd={() => setShowPasswordFields(false)}
                        >
                          <RemoveRedEyeOutlined />
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <ControlledField
                control={control}
                name='confirmPassword'
                fieldProps={{
                  InputProps: {
                    type: showPasswordFields ? 'text' : 'password',
                    autoComplete: 'new-password',
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton
                          tabIndex={-1}
                          onMouseDown={() => setShowPasswordFields(true)}
                          onMouseUp={() => setShowPasswordFields(false)}
                          onMouseLeave={() => setShowPasswordFields(false)}
                          onTouchStart={() => setShowPasswordFields(true)}
                          onTouchEnd={() => setShowPasswordFields(false)}
                        >
                          <RemoveRedEyeOutlined />
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <FormControlGroup error={errors?.termsAccepted} required>
                <ControlledField
                  control={control}
                  name='termsAccepted'
                  render={(renderProps) => <CheckboxInputField {...renderProps} />}
                />
              </FormControlGroup>
            </Stack>
          </Grid>
          <Button
            type='submit'
            disabled={isSubmitting || isValidating || isLoading}
            variant='contained'
            sx={{ display: 'flex', maxWidth: 300, ml: 'auto', mt: '1rem' }}
          >
            {t('actions.user.createAccount')}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
}

export default AccountInvitationRegistrationForm;
